import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { Tab, TextField, useGetList, useRecordContext } from "react-admin";
import { useMemo } from "react";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { PageHeader } from "@/components/common/PageHeader";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { PageContent, TabContent } from "@/components/common/PageLayout";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { StyledDatagrid } from "@/components/ui/react-admin/StyledDatagrid";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { Permission, ResourceName, Role, User } from "@/types";
import { getResourceIcon } from "@/utils/icons";
import { PermissionsDisplay } from "./components/PermissionsDisplay";

interface RoleUserAssignment {
  user: User;
  assigned_datetime?: string;
  is_inherited?: boolean;
  assigned_by_user_id?: string;
  expires_datetime?: string;
  scope?: string;
}

const LoadingPanel = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
    <CircularProgress size={48} />
  </Box>
);

const RoleSummary = () => {
  const record = useRecordContext<Role>();
  if (!record) return null;

  return (
    <Box sx={{ p: 2 }}>
      {record.description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {record.description}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          label={record.is_system_role ? "System Role" : "Custom Role"}
          size="small"
          color={record.is_system_role ? "primary" : "default"}
          variant="outlined"
        />
        <Chip
          label={`${record.user_count ?? 0} user${record.user_count === 1 ? "" : "s"}`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={`${record.permission_count ?? 0} permission${record.permission_count === 1 ? "" : "s"}`}
          size="small"
          variant="outlined"
        />
      </Stack>
    </Box>
  );
};

const RolePermissionsSection = () => {
  const record = useRecordContext<Role>();
  const {
    data: permissions,
    isLoading,
    isError,
  } = useGetList<Permission>(
    `${ResourceName.ROLES}/${record?.id}/permissions`,
    { pagination: { page: 1, perPage: 500 } },
    { enabled: !!record?.id }
  );

  if (isLoading) return <LoadingPanel />;
  if (isError) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Failed to load permissions.
      </Typography>
    );
  }
  return <PermissionsDisplay permissions={permissions ?? []} />;
};

const RoleOverviewTab = () => (
  <Box>
    <ShowAccordionSection label="Role Information" defaultExpanded>
      <RoleSummary />
    </ShowAccordionSection>

    <ShowAccordionSection label="Permissions" defaultExpanded>
      <RolePermissionsSection />
    </ShowAccordionSection>
  </Box>
);

const RoleUsersTab = () => {
  const record = useRecordContext<Role>();
  const {
    data: assignments,
    isLoading,
    isError,
  } = useGetList<RoleUserAssignment & { id: string }>(
    `${ResourceName.ROLES}/${record?.id}/users`,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "full_name", order: "ASC" },
    },
    { enabled: !!record?.id }
  );

  const users = useMemo(() => assignments?.map((a) => a.user).filter(Boolean) ?? [], [assignments]);

  if (isLoading) return <LoadingPanel />;

  if (isError) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Failed to load users.
      </Typography>
    );
  }

  if (!users || users.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No users assigned to this role.
      </Typography>
    );
  }

  return (
    <StyledDatagrid
      data={users}
      bulkActionButtons={false}
      rowClick={(id) => `/users/${id}/show`}
      sx={{ backgroundColor: "background.paper" }}
    >
      <TextField source="id" label="ID" />
      <TextField source="full_name" label="Name" />
      <TextField source="job_title" label="Title" />
      <TextField source="email" label="Email" />
      <StatusChipField source="status" />
    </StyledDatagrid>
  );
};

export const RolesShow = () => {
  const RoleIcon = getResourceIcon(ResourceName.ROLES);

  return (
    <StyledShow actions={false}>
      <PageHeader
        titleKey="name"
        subtitleKey="aegle_id"
        headerIcon={<RoleIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <PageContent>
        <StyledTabbedShowLayout>
          <Tab label="Overview">
            <TabContent>
              <RoleOverviewTab />
            </TabContent>
          </Tab>
          <Tab label="Users">
            <TabContent>
              <RoleUsersTab />
            </TabContent>
          </Tab>
          <Tab label="Audit Log">
            <AuditLogBox tableName={ResourceName.ROLES} />
          </Tab>
        </StyledTabbedShowLayout>
      </PageContent>
    </StyledShow>
  );
};
